import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatDateTimeBR } from "../lib/api";
import { toast } from "sonner";
import {
  Wrench, Coins, Building2, DollarSign, TrendingUp, Link2, Wallet,
  FileText, Layers, LifeBuoy, X, Loader2, CheckCircle2, AlertTriangle,
  History, ChevronDown, ChevronRight,
} from "lucide-react";

const ACTIONS = [
  {
    key: "tesouraria-socios",
    label: "Recalcular Tesouraria dos Sócios",
    description: "Reprocessa saldos, capital na rua e lucro de todos os sócios usando a lógica canônica (_socio_with_stats).",
    icon: Coins,
    color: "text-[#D4AF37]",
    destructive: false,
  },
  {
    key: "tesouraria-empresa",
    label: "Recalcular Tesouraria da Empresa",
    description: "Reconcilia os lançamentos do company_ledger (Capital Próprio) e contabiliza contratos empresa ativos/quitados.",
    icon: Building2,
    color: "text-violet-300",
    destructive: false,
  },
  {
    key: "capital-disponivel",
    label: "Recalcular Capital Disponível",
    description: "Reexecuta o cálculo de capital disponível para cada sócio ativo, a partir de contratos, pagamentos e ledger.",
    icon: DollarSign,
    color: "text-emerald-300",
    destructive: false,
  },
  {
    key: "lucro-disponivel",
    label: "Recalcular Lucro Disponível",
    description: "Reexecuta o cálculo de lucro disponível para saque de cada sócio ativo (juros recebidos − ledger).",
    icon: TrendingUp,
    color: "text-emerald-300",
    destructive: false,
  },
  {
    key: "indicadores",
    label: "Recalcular Indicadores",
    description: "Recomputa a contagem de clientes e clientes aprovados por indicador.",
    icon: Link2,
    color: "text-blue-300",
    destructive: false,
  },
  {
    key: "comissoes",
    label: "Recalcular Comissões",
    description: "Recomputa a comissão devida de cada cliente aprovado usando a regra oficial (approved_amount × %).",
    icon: Wallet,
    color: "text-blue-300",
    destructive: false,
  },
  {
    key: "contratos",
    label: "Recalcular Contratos",
    description: "Detecta contratos 'presos' (status=ativo mas saldo=0) e os move para 'quitado' formalmente, com trilha de auditoria.",
    icon: FileText,
    color: "text-amber-300",
    destructive: true,
  },
  {
    key: "parcelados",
    label: "Recalcular Parcelados e Acordos",
    description: "Recomputa balance_remaining de cada parcelado/acordo a partir das parcelas em aberto e persiste divergências.",
    icon: Layers,
    color: "text-amber-300",
    destructive: true,
  },
  {
    key: "recuperacao",
    label: "Recalcular Recuperação",
    description: "Fecha automaticamente registros de recuperação cujos contratos/parcelados já foram quitados ou cancelados.",
    icon: LifeBuoy,
    color: "text-amber-300",
    destructive: true,
  },
  {
    key: "reconcile-advances-renewals",
    label: "Reconciliar Antecipações × Renovações",
    description: "Corrige históricos onde a renovação ocorreu com antecipação aberta no mesmo ciclo e o sócio recebeu crédito duplicado. Marca antecipações como compensadas e redireciona o socio_share para a empresa.",
    icon: Wrench,
    color: "text-red-300",
    destructive: true,
  },
];

function ConfirmationModal({ action, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [running, setRunning] = useState(false);
  if (!action) return null;
  const Icon = action.icon;

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) {
      toast.error("Motivo precisa ter ao menos 5 caracteres");
      return;
    }
    setRunning(true);
    try {
      await onConfirm(action, reason.trim());
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="ajuste-confirm-modal"
    >
      <div className="fc-card w-full max-w-lg my-8 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <Icon size={20} className={action.color} />
            <div>
              <h3 className="font-display text-lg font-semibold">{action.label}</h3>
              <p className="text-[12px] text-[#A3A3A3] mt-1">{action.description}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-white" data-testid="ajuste-modal-close">
            <X size={16} />
          </button>
        </div>
        {action.destructive && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 mb-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 flex-shrink-0" />
            <div className="text-[12px] text-amber-200">
              Esta ação <strong>persiste alterações</strong> no banco (status de contrato, saldo remanescente, etc.).
              Trilha de auditoria será registrada.
            </div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">
              Motivo (obrigatório, mín. 5 caracteres)
            </label>
            <textarea
              className="fc-input w-full text-[13px]"
              rows={3}
              required
              minLength={5}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: reconciliação mensal após homologação"
              data-testid="ajuste-reason-input"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="fc-btn-ghost text-xs"
              data-testid="ajuste-cancel-btn"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={running}
              className="fc-btn-gold flex items-center gap-1.5 text-xs"
              data-testid="ajuste-confirm-btn"
            >
              {running ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              Confirmar e executar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResultCard({ result, onClose }) {
  const [expanded, setExpanded] = useState(false);
  if (!result) return null;
  const { message, records_processed, records_updated, records_with_issues, details, audit_id } = result;
  return (
    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2" data-testid="ajuste-result-card">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} className="text-emerald-300" />
          <div className="font-display text-sm font-semibold text-emerald-200" data-testid="ajuste-result-message">
            {message}
          </div>
        </div>
        <button onClick={onClose} className="text-[#737373] hover:text-white">
          <X size={14} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[12px]">
        <div className="rounded bg-[#0a0a0a] border border-[#262626] px-2 py-1.5">
          <div className="text-[10px] text-[#737373]">Processados</div>
          <div className="font-mono-tab" data-testid="ajuste-result-processed">{records_processed}</div>
        </div>
        <div className="rounded bg-[#0a0a0a] border border-[#262626] px-2 py-1.5">
          <div className="text-[10px] text-[#737373]">Atualizados</div>
          <div className={`font-mono-tab ${records_updated > 0 ? "text-amber-300" : ""}`} data-testid="ajuste-result-updated">
            {records_updated}
          </div>
        </div>
        <div className="rounded bg-[#0a0a0a] border border-[#262626] px-2 py-1.5">
          <div className="text-[10px] text-[#737373]">Com issues</div>
          <div className={`font-mono-tab ${records_with_issues > 0 ? "text-amber-300" : ""}`} data-testid="ajuste-result-issues">
            {records_with_issues}
          </div>
        </div>
      </div>
      {audit_id && (
        <div className="text-[10.5px] text-[#737373]">
          Audit: <span className="font-mono">{audit_id.slice(0, 8)}…</span>
        </div>
      )}
      {details && details.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] text-[#D4AF37] hover:text-[#E8C547] flex items-center gap-1"
          data-testid="ajuste-toggle-details"
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {expanded ? "Ocultar" : "Ver"} detalhes ({details.length})
        </button>
      )}
      {expanded && (
        <pre className="text-[10.5px] font-mono bg-black/40 rounded p-2 max-h-64 overflow-y-auto text-[#A3A3A3]">
          {JSON.stringify(details, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function CentralAjustesPage() {
  const [selectedAction, setSelectedAction] = useState(null);
  const [results, setResults] = useState({}); // key -> last result
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/adjustments/history", { params: { limit: 30 } });
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Falha ao carregar histórico");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const runAction = async (action, reason) => {
    try {
      const { data } = await api.post(`/adjustments/${action.key}`, { reason });
      setResults((r) => ({ ...r, [action.key]: data }));
      toast.success(data.message || "Recálculo concluído");
      setSelectedAction(null);
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao executar recálculo");
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-6" data-testid="central-ajustes-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#D4AF37] mb-1">Administração</div>
            <h1 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-2">
              <Wrench size={22} className="text-[#D4AF37]" /> Central de Ajustes
            </h1>
            <p className="text-[13px] text-[#A3A3A3] mt-1 max-w-2xl">
              Reconstrói saldos e reconcilia estados usando exclusivamente a lógica oficial do sistema.
              Cada ação exige motivo, registra auditoria e reporta a quantidade de registros recalculados.
              <br />
              <span className="text-amber-300">Nenhum ajuste manual de valor é permitido</span> — apenas recálculo automático.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="fc-btn-ghost flex items-center gap-1.5 text-xs"
            data-testid="ajuste-toggle-history"
          >
            <History size={14} /> Histórico
          </button>
        </div>

        {historyOpen && (
          <section className="fc-card p-4" data-testid="ajuste-history-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-sm font-semibold flex items-center gap-2">
                <History size={14} className="text-[#D4AF37]" /> Últimas execuções
              </h2>
              {historyLoading && <Loader2 size={12} className="animate-spin text-[#737373]" />}
            </div>
            {history.length === 0 ? (
              <div className="text-[13px] text-[#737373]">Nenhuma execução registrada ainda.</div>
            ) : (
              <ul className="space-y-1.5 max-h-80 overflow-y-auto">
                {history.map((h) => (
                  <li key={h.id} className="text-[12px] border-b border-[#1a1a1a] pb-1.5" data-testid={`ajuste-history-${h.kind}`}>
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <span className="text-white font-medium">{h.kind}</span>
                      <span className="text-[#737373]">{formatDateTimeBR(h.executed_at)}</span>
                      <span className="text-[#A3A3A3]">por {h.user_email || "—"}</span>
                    </div>
                    <div className="text-[11px] text-[#A3A3A3] mt-0.5">
                      Processados: <span className="font-mono-tab">{h.records_processed}</span>
                      {" · "}Atualizados: <span className={`font-mono-tab ${h.records_updated > 0 ? "text-amber-300" : ""}`}>{h.records_updated}</span>
                      {" · "}Issues: <span className={`font-mono-tab ${h.records_with_issues > 0 ? "text-amber-300" : ""}`}>{h.records_with_issues}</span>
                    </div>
                    {h.reason && <div className="text-[10.5px] text-[#737373] italic mt-0.5">Motivo: {h.reason}</div>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="space-y-3">
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            const result = results[a.key];
            return (
              <div
                key={a.key}
                className="fc-card p-4 sm:p-5"
                data-testid={`ajuste-card-${a.key}`}
              >
                <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
                  <div className="mt-0.5">
                    <Icon size={20} className={a.color} />
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-[15px] font-semibold" data-testid={`ajuste-label-${a.key}`}>
                        {a.label}
                      </h3>
                      {a.destructive && (
                        <span className="badge-base bg-amber-500/15 text-amber-200 border-amber-500/40 text-[10px]">
                          Persiste alterações
                        </span>
                      )}
                    </div>
                    <p className="text-[12.5px] text-[#A3A3A3] mt-1">{a.description}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setSelectedAction(a)}
                      className="fc-btn-gold text-xs flex items-center gap-1.5"
                      data-testid={`ajuste-exec-${a.key}`}
                    >
                      <Wrench size={12} /> Executar
                    </button>
                  </div>
                </div>
                {result && (
                  <div className="mt-3">
                    <ResultCard
                      result={result}
                      onClose={() => setResults((r) => { const c = { ...r }; delete c[a.key]; return c; })}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </div>

      {selectedAction && (
        <ConfirmationModal
          action={selectedAction}
          onClose={() => setSelectedAction(null)}
          onConfirm={runAction}
        />
      )}
    </Layout>
  );
}
